import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { MFAMethods } from '../domain/AuthMethods';
import { useAuthAPI } from '../services/auth_api/AuthAPIHook';
import { authAPI } from '../services/auth_api/SessionAuthAPI';
import { errorInvalidCredentials } from '../services/auth_api/Errors';
import UserInfo from '../components/UserInfo';
import '../styles/Login.css';
import '../styles/Spinner.css';

const LoginPassword: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { submitPassword } = useAuthAPI(authAPI);
    const { sessionData, setSessionData } = useSession();
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false); // New loading state



    useEffect(() => {
        if (!sessionData) {
            console.log("No session data found");
            navigate('/login/error', {
                state: {
                  errorMessage: t('ui_login_bad_request'),
                  error: 'bad_request',
                }
              });
        }
    }, [sessionData, navigate, t]);

    if (!sessionData) {
        return null; // Return null while redirecting to avoid rendering the component
    }

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    };

    const handleNext = async () => {
        setLoading(true);
        try {
            const res = await submitPassword(password, sessionData.session_id);
            if (MFAMethods.some(el => res.available_auth_methods.includes(el))) {
                navigate('/login/mfa');
                return;
            }
            setSessionData(res);
            navigate('/login/consent');
        } catch (err) {
            if (err === errorInvalidCredentials) {
                setError(t('error_invalid_password'));
                return;
            }
            navigate('/login/error', {
                state: {
                    errorMessage: t('error_unexpected'),
                    error: 'internal_error',
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/login/error', {
            state: {
              errorMessage: t('error_login_cancelled'),
              error: 'login_cancelled',
            }
          });
    };

    return (
        <div className="login-container">
            <div className="logo"></div> {/* Placeholder for the logo */}
            <h2 className="login-title">{t('ui_login')}</h2>
            <UserInfo sessionData={sessionData} />
            <div className="login-box">
                <label htmlFor="password">{t('ui_password')}</label>
                <div className="input-container">
                    <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={handlePasswordChange}
                        placeholder={t('ui_enter_password')}
                    />
                    {error && <span className="error-message">{error}</span>}
                </div>
                {loading ? (
                    <div className="spinner-container">
                        <div className="spinner"></div> {/* Add spinner styles here */}
                    </div>
                ) : (
                    <>
                        <button onClick={handleNext} className="submit-button">{t('ui_next')}</button>
                        <button onClick={handleCancel} className="back-button">{t('ui_cancel')}</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginPassword;
